import { useEffect, useRef, useState } from 'react';

import { DraftModel, PetBlockModel } from '../models/QuickSaleModel';
import { simulateDraft } from '../services/QuickSaleCRUD';
import { flattenErrors } from '../utils/quickSaleFormat';

// Waits for the agent to stop typing before pricing the draft.
export const SIMULATION_DEBOUNCE_MS = 600;

type DraftSection = keyof DraftModel;

function hasValues(section?: object) {
  return !!section && Object.keys(section).length > 0;
}

export function isDraftDirty(draft: DraftModel) {
  return (Object.keys(draft) as DraftSection[]).some((section) =>
    hasValues(draft[section])
  );
}

/** Whether the draft changes anything a price depends on. */
export function draftAffectsPrice(draft: DraftModel) {
  return (
    hasValues(draft.pet) || hasValues(draft.plan) || hasValues(draft.address)
  );
}

/**
 * The quick-sale draft of one pet and one plan.
 *
 * Every change that can move a price is simulated by the backend (applied and
 * rolled back), so what the agent reads is what saving would produce.
 */
export function useDraft(
  userId: number,
  petId: number | null,
  planId: number | null,
  // Changes when the saved data is reloaded (a coupon, an order edit): the
  // draft is kept and priced again on top of it.
  refreshKey?: unknown
) {
  const [draft, setDraft] = useState<DraftModel>({});
  const [simulation, setSimulation] = useState<PetBlockModel | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const latestRequest = useRef(0);

  function reset() {
    latestRequest.current += 1;
    setDraft({});
    setSimulation(null);
    setSimulating(false);
    setErrors([]);
  }

  useEffect(reset, [petId, planId]);

  /** Merges ``values`` into a section; ``undefined`` values are dropped. */
  function update<K extends DraftSection>(
    section: K,
    values: Partial<NonNullable<DraftModel[K]>>
  ) {
    setDraft((previous) => {
      const merged: Record<string, unknown> = {
        ...(previous[section] || {}),
        ...values,
      };
      Object.keys(merged).forEach((key) => {
        if (merged[key] === undefined) {
          delete merged[key];
        }
      });
      const next = { ...previous };
      const onlyId = section === 'address' && Object.keys(merged).length <= 1;
      if (Object.keys(merged).length === 0 || onlyId) {
        delete next[section];
      } else {
        next[section] = merged as DraftModel[K];
      }
      return next;
    });
  }

  function clear(section: DraftSection) {
    setDraft((previous) => {
      const next = { ...previous };
      delete next[section];
      return next;
    });
  }

  useEffect(() => {
    if (!userId || !petId || !draftAffectsPrice(draft)) {
      latestRequest.current += 1;
      setSimulation(null);
      setSimulating(false);
      setErrors([]);
      return undefined;
    }

    latestRequest.current += 1;
    const requestId = latestRequest.current;
    const timer = setTimeout(async () => {
      setSimulating(true);
      try {
        const { data } = await simulateDraft(userId, {
          pet_id: petId,
          plan_id: planId,
          draft,
        });
        if (requestId === latestRequest.current) {
          setSimulation(data);
          setErrors([]);
        }
      } catch (error: any) {
        if (requestId === latestRequest.current) {
          setSimulation(null);
          setErrors(flattenErrors(error?.response?.data));
        }
      } finally {
        if (requestId === latestRequest.current) {
          setSimulating(false);
        }
      }
    }, SIMULATION_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [draft, userId, petId, planId, refreshKey]);

  return {
    draft,
    update,
    clear,
    reset,
    isDirty: isDraftDirty(draft),
    simulation,
    simulating,
    errors,
  };
}

export type DraftState = ReturnType<typeof useDraft>;
